// Guest Session Management Utility
// Generate a simple UUID alternative since uuid package might not be available
import { recalculateItemPricing } from './pricingService';
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const GUEST_SESSION_KEY = 'podvolution_guest_session';
const GUEST_CART_KEY = 'podvolution_guest_cart';
const FREE_IMAGE_LIMIT = 5;

// Generate a device fingerprint for persistent tracking
const generateDeviceFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Device fingerprint', 2, 2);
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    canvas.toDataURL(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.deviceMemory || 'unknown'
  ].join('|');
  
  // Create a simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  const result = Math.abs(hash).toString(36);
  console.log("🔐 Generated device fingerprint:", result);
  return result;
};

// Check if localStorage is available and has space
const checkLocalStorageAvailability = () => {
  try {
    const testKey = 'test_storage_availability';
    const testValue = 'test';
    localStorage.setItem(testKey, testValue);
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.error('localStorage is not available:', error);
    return false;
  }
};

// Get localStorage usage info
const getLocalStorageUsage = () => {
  try {
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += localStorage[key].length;
      }
    }
    return {
      totalSize,
      available: true
    };
  } catch (error) {
    return {
      totalSize: 0,
      available: false,
      error: error.message
    };
  }
};

// Generate or get existing guest session ID
// Cache to prevent repeated fingerprint generation
let cachedSession = null;
let sessionCacheTime = 0;
const SESSION_CACHE_DURATION = 1000; // Cache for 1 second

export const getGuestSessionId = () => {
  // Return cached session if available and still valid
  const now = Date.now();
  if (cachedSession && (now - sessionCacheTime) < SESSION_CACHE_DURATION) {
    return cachedSession;
  }
  
  let session = localStorage.getItem(GUEST_SESSION_KEY);
  
  if (!session) {
    const deviceFingerprint = generateDeviceFingerprint();
    const newSession = {
      id: generateUUID(),
      deviceFingerprint: deviceFingerprint,
      createdAt: new Date().toISOString(),
      imagesGenerated: 0,
      freeImagesUsed: 0
    };
    console.log("Creating new guest session with fingerprint:", { ...newSession, deviceFingerprint: deviceFingerprint.substring(0, 8) + '...' });
    localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(newSession));
    cachedSession = newSession;
    sessionCacheTime = now;
    return newSession;
  }
  
  const parsedSession = JSON.parse(session);
  
  // Update device fingerprint if it's missing (only once)
  if (!parsedSession.deviceFingerprint) {
    const deviceFingerprint = generateDeviceFingerprint();
    parsedSession.deviceFingerprint = deviceFingerprint;
    localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(parsedSession));
    console.log("Added device fingerprint to existing session");
  }
  
  // Cache the session
  cachedSession = parsedSession;
  sessionCacheTime = now;
  
  return parsedSession;
};

// Check if guest can generate free image
export const canGenerateFreeImage = () => {
  const session = getGuestSessionId();
  return session.freeImagesUsed < FREE_IMAGE_LIMIT;
};

// Get remaining free images count
export const getFreeImagesRemaining = () => {
  const session = getGuestSessionId();
  return Math.max(0, FREE_IMAGE_LIMIT - session.freeImagesUsed);
};

// Increment free image usage
export const incrementFreeImageUsage = () => {
  const session = getGuestSessionId();
  session.freeImagesUsed += 1;
  session.imagesGenerated += 1;
  localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
  return session;
};

// Sync guest session with backend response
export const syncGuestSessionFromBackend = (backendResponse) => {
  console.log("🔄 syncGuestSessionFromBackend called with:", backendResponse);
  if (backendResponse && typeof backendResponse.free_images_remaining === 'number') {
    const session = getGuestSessionId();
    const FREE_IMAGE_LIMIT = 5;
    const oldUsed = session.freeImagesUsed;
    session.freeImagesUsed = FREE_IMAGE_LIMIT - backendResponse.free_images_remaining;
    
    console.log("🔄 SYNC CALCULATION:");
    console.log("   FREE_IMAGE_LIMIT:", FREE_IMAGE_LIMIT);
    console.log("   backend.free_images_remaining:", backendResponse.free_images_remaining);
    console.log("   calculated freeImagesUsed:", FREE_IMAGE_LIMIT - backendResponse.free_images_remaining);
    console.log("   oldUsed:", oldUsed);
    console.log("   newUsed:", session.freeImagesUsed);
    
    localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(session));
    
    // Verify localStorage was updated
    const verifySession = JSON.parse(localStorage.getItem(GUEST_SESSION_KEY));
    console.log("🔄 Verified localStorage update:", verifySession);
    
    console.log("🔄 Synced guest session with backend:", {
      remaining: backendResponse.free_images_remaining,
      oldUsed: oldUsed,
      newUsed: session.freeImagesUsed,
      sessionId: session.id
    });
    return session;
  } else {
    console.log("🚫 Cannot sync - invalid backend response:", backendResponse);
    console.log("🚫 Expected object with free_images_remaining number, got:", typeof backendResponse?.free_images_remaining);
  }
};

// Reset guest session (useful for debugging or clearing limits)
export const resetGuestSession = () => {
  const newSession = {
    id: generateUUID(),
    deviceFingerprint: generateDeviceFingerprint(),
    createdAt: new Date().toISOString(),
    imagesGenerated: 0,
    freeImagesUsed: 0
  };
  localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(newSession));
  console.log("🔄 Guest session reset:", newSession);
  return newSession;
};

// Get guest cart items
export const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    if (!cart) {
      return [];
    }
    
    const parsedCart = JSON.parse(cart);
    console.log("getGuestCart - raw cart:", cart);
    console.log("getGuestCart - parsed cart:", parsedCart);
    
    // Validate cart structure
    if (!Array.isArray(parsedCart)) {
      console.warn("Invalid cart structure, returning empty cart");
      return [];
    }
    
    return parsedCart;
  } catch (error) {
    console.error("Error reading guest cart from localStorage:", error);
    // Clear corrupted cart data
    try {
      localStorage.removeItem(GUEST_CART_KEY);
    } catch (clearError) {
      console.error("Failed to clear corrupted cart:", clearError);
    }
    // Return empty cart if there's an error
    return [];
  }
};

// Add item to guest cart
export const addToGuestCart = (item) => {
  try {
    console.log("addToGuestCart called with item:", item);
    
    const cart = getGuestCart();
    console.log("Current cart:", cart);
    
    // Check if cart is getting too large (prevent quota exceeded)
    const maxCartItems = 5; // Reduced from 10 to 5 items to prevent quota issues
    if (cart.length >= maxCartItems) {
      console.warn("Cart is full. Removing oldest item to make space.");
      cart.shift(); // Remove oldest item
    }
    
    const existingItemIndex = cart.findIndex(cartItem => 
      cartItem.product_id === item.product_id &&
      cartItem.variant_id === item.variant_id &&
      cartItem.size === item.size &&
      cartItem.option_color === item.option_color
    );

    if (existingItemIndex >= 0) {
      console.log("Updating existing item at index:", existingItemIndex);
      const existingItem = cart[existingItemIndex];
      
      // Use centralized pricing service for consistent calculations
      const updatedItem = recalculateItemPricing(existingItem, existingItem.quantity + item.quantity);
      
      // Update the existing item with new pricing
      Object.assign(existingItem, updatedItem);
      
      console.log('🔄 Existing item update calculation:', {
        oldQuantity: existingItem.quantity - item.quantity,
        newQuantity: existingItem.quantity,
        newTotalCost: existingItem.total_cost,
        newShippingCost: existingItem.shipping_cost
      });
    } else {
      console.log("Adding new item to cart");
      // Create minimal cart item to reduce storage size
      const cartItem = {
        product_id: item.product_id,
        variant_id: item.variant_id,
        size: item.size,
        option_color: item.option_color,
        quantity: item.quantity,
        unit_cost: item.unit_cost || (item.total_cost / Math.max(item.quantity, 1)),
        total_cost: item.total_cost,
        product_title: item.product_title,
        cart_id: generateUUID(),
        created_at: new Date().toISOString(),
        // Store pricing breakdown
        product_price: item.product_price || 0,
        shipping_cost: item.shipping_cost || 0,
        tax_amount: item.tax_amount || 0,
        // Store full image URLs
        image_url_1: item.image_url_1 || "",
        image_url_2: item.image_url_2 || "",
        // Store additional product details for checkout
        provider_id: item.provider_id,
        country: item.country || "US",
        misc: item.misc || {}
      };
      cart.push(cartItem);
    }

    console.log("Updated cart:", cart);
    
    // Try to save to localStorage with improved error handling
    try {
      const cartString = JSON.stringify(cart);
      // Check if the cart string is too large (over 1MB)
      if (cartString.length > 1000000) {
        console.warn("Cart data too large, removing oldest items");
        // Remove oldest items until cart is under 500KB
        while (cart.length > 1 && JSON.stringify(cart).length > 500000) {
          cart.shift();
        }
      }
      
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
      console.log("Successfully saved cart to localStorage");
    } catch (storageError) {
      console.error("localStorage quota exceeded. Attempting to clear and retry...");
      
      // Try to clear other guest data first
      try {
        localStorage.removeItem(GUEST_SESSION_KEY);
        localStorage.removeItem(GUEST_CART_KEY);
      } catch (clearError) {
        console.error("Failed to clear localStorage:", clearError);
      }
      
      // Create minimal cart with just this item
      const minimalCart = [{
        product_id: item.product_id,
        variant_id: item.variant_id,
        size: item.size,
        option_color: item.option_color,
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        total_cost: item.total_cost,
        product_title: item.product_title,
        cart_id: generateUUID(),
        created_at: new Date().toISOString(),
        // Store pricing breakdown
        product_price: item.product_price || 0,
        shipping_cost: item.shipping_cost || 0,
        tax_amount: item.tax_amount || 0,
        // Store essential details
        provider_id: item.provider_id,
        country: item.country || "US"
      }];
      
      try {
        localStorage.setItem(GUEST_CART_KEY, JSON.stringify(minimalCart));
        console.log("Successfully saved minimal cart to localStorage");
        window.dispatchEvent(new Event('guestCartUpdated'));
        return minimalCart;
              } catch (finalError) {
          console.error("Failed to save even minimal cart:", finalError);
          // Show user-friendly error message
          const errorMessage = "Cart storage is full. Please clear some browser data or remove items from cart.";
          console.error(errorMessage);
          const error = new Error(errorMessage);
          showStorageError(error);
          throw error;
        }
    }
    
    // Dispatch custom event to notify navbar
    window.dispatchEvent(new Event('guestCartUpdated'));
    
    return cart;
  } catch (error) {
    console.error("Error in addToGuestCart:", error);
    throw error;
  }
};

// Remove item from guest cart
export const removeFromGuestCart = (cartId) => {
  const cart = getGuestCart();
  const filteredCart = cart.filter(item => item.cart_id !== cartId);
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(filteredCart));
  
  // Dispatch custom event to notify navbar
  window.dispatchEvent(new Event('guestCartUpdated'));
  
  return filteredCart;
};

// Update guest cart item quantity
export const updateGuestCartQuantity = (cartId, newQuantity) => {
  const cart = getGuestCart();
  const itemIndex = cart.findIndex(item => item.cart_id === cartId);
  
  if (itemIndex >= 0) {
    if (newQuantity <= 0) {
      cart.splice(itemIndex, 1);
    } else {
      const item = cart[itemIndex];
      
      // Use centralized pricing service for consistent calculations
      const updatedItem = recalculateItemPricing(item, newQuantity);
      
      // Update the item in the cart
      cart[itemIndex] = updatedItem;
    }
  }
  
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  
  // Dispatch custom event to notify navbar
  window.dispatchEvent(new Event('guestCartUpdated'));
  
  return cart;
};

// Clear guest cart
export const clearGuestCart = () => {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
    console.log("Guest cart cleared successfully");
    // Dispatch event to notify components
    window.dispatchEvent(new Event('guestCartUpdated'));
  } catch (error) {
    console.error("Error clearing guest cart:", error);
  }
};

// Clear all guest data (cart and session)
export const clearAllGuestData = () => {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.removeItem(GUEST_SESSION_KEY);
    console.log("All guest data cleared successfully");
    // Dispatch event to notify components
    window.dispatchEvent(new Event('guestCartUpdated'));
  } catch (error) {
    console.error("Error clearing guest data:", error);
  }
};

// Get storage usage info for debugging
export const getGuestStorageInfo = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    const session = localStorage.getItem(GUEST_SESSION_KEY);
    
    return {
      cartSize: cart ? cart.length : 0,
      sessionSize: session ? session.length : 0,
      totalSize: (cart ? cart.length : 0) + (session ? session.length : 0),
      cartItems: cart ? JSON.parse(cart).length : 0
    };
  } catch (error) {
    console.error("Error getting storage info:", error);
    return { error: error.message };
  }
};

// Helper function to show storage error message to user
export const showStorageError = (error) => {
  const message = error.message || "Storage error occurred";
  
  // Try to show toast notification if available
  if (window.toast) {
    window.toast.error(message);
  } else {
    // Fallback to alert
    alert(message);
  }
  
  console.error("Storage error:", error);
};

// Helper function to handle storage errors gracefully
export const handleStorageError = (error, fallbackAction = null) => {
  showStorageError(error);
  
  // If a fallback action is provided, execute it
  if (fallbackAction && typeof fallbackAction === 'function') {
    try {
      fallbackAction();
    } catch (fallbackError) {
      console.error("Fallback action failed:", fallbackError);
    }
  }
};

// Check if user is guest (not logged in)
export const isGuestUser = () => {
  const userData = localStorage.getItem("userData");
  if (!userData) return true;
  
  try {
    const user = JSON.parse(userData);
    return !user.access_token && !user.id_token;
  } catch {
    return true;
  }
};

// Get user ID (guest session ID for guests, actual user ID for logged in users)
export const getUserId = () => {
  if (isGuestUser()) {
    return getGuestSessionId().id;
  }
  
  const userData = localStorage.getItem("userData");
  if (userData) {
    try {
      const user = JSON.parse(userData);
      if (user.id_token) {
        // Import jwtDecode dynamically to avoid issues
        try {
          const jwtDecode = require('jwt-decode').jwtDecode || require('jwt-decode');
          return jwtDecode(user.id_token).user_id;
        } catch (error) {
          console.error('Error decoding JWT:', error);
          return getGuestSessionId().id;
        }
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
    }
  }
  
  return getGuestSessionId().id;
};

// Transfer guest cart to user account after login
export const transferGuestCartToUser = async (userId) => {
  const guestCart = getGuestCart();
  if (guestCart.length === 0) return;

  // Here you would typically make API calls to transfer cart items
  // For now, we'll just clear the guest cart
  clearGuestCart();
  
  return guestCart;
}; 